import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Info, RefreshCw, Sparkles } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import type { GroomingQueueActionResult, GroomingRunnerEvent } from "@ttc-spectree/toolbox-contract";
import { CommandCopyBox } from "@/components/common/command-copy-box";
import { BriefReview } from "@/components/grooming/brief-review";
import { briefDay } from "@/components/grooming/brief-state-bar";
import { BriefStateChip } from "@/components/grooming/brief-state-chip";
import { NextInLineCard } from "@/components/grooming/next-in-line-card";
import {
  QueueBadge,
  activeEntryByKey,
  safeQueueKey,
  useOnQueueSettled,
  useQueueBadgeStatus,
  useQueueStart,
} from "@/components/grooming/queue-status-badge";
import { stageOfItem } from "@/components/grooming/reading-pane";
import { RunActivity, useRunProgress, useSupportsRunProgress } from "@/components/grooming/run-activity";
import { RunnerFallback } from "@/components/grooming/runner-fallback";
import { PersonaPicker, usePersonaChoice } from "@/components/toolbox/persona-picker";
import { Button } from "@/components/ui/button";
import { groomingKeys, useGroomingProjects, useProjectSession } from "@/hooks/queries/use-grooming";
import { canOn, useToolboxAccess } from "@/hooks/queries/use-toolbox-access";
import { useWorkItem, workItemKeys } from "@/hooks/queries/use-work-items";
import { useGroomingQueue } from "@/hooks/use-grooming-queue";
import { appPutsSingleItemsNextInLine } from "@/lib/grooming/next-in-line";
import { usePageTitle } from "@/hooks/use-page-title";
import { formulatorImprovePath } from "@/lib/api/work-item-interview";
import { workItemsApi, type GroomingBrief, type WorkItemDetail } from "@/lib/api/work-items";
import { groomingErrorMessage, groomingFixPath, stopReasonMessage } from "@/lib/grooming/error-messages";
import { groomingPath } from "@/lib/grooming/paths";
import { briefStateOf, confidenceWords, groomCommand } from "@/lib/grooming/plain-words";
import {
  recentItemState,
  recentItemStateWords,
  recordRecentItem,
  updateRecentItem,
  useRecentItems,
  type RecentItem,
  type RecentItemState,
} from "@/lib/grooming/recent-items";
import { useRunnerBridge, useRunnerEvents } from "@/lib/grooming/runner-bridge";
import { cn } from "@/lib/utils";
import { TOOLBOX_PAGE_TITLE, TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";

/**
 * One item (epic PI-SPEC-SW-E279, task PI-SPEC-SW-1392-4; Claude Design
 * "All Screens" frame 2g): groom a single item from an id, AB#id or ADO
 * link. Grooming needs the `groom-one` capability on the item's project
 * (Admin, Project Manager, Product Owner; epic PI-SPEC-SW-E283); everyone
 * else gets a read-only "Show" that opens the item's latest brief.
 *
 * In SpecTree Toolbox, Groom runs the item on the person's own Claude seat
 * and shows the result card. While it runs, a contract-2 app reports the
 * run's phase and a live activity line (E280, feature PI-SPEC-SW-1403):
 * the page shows them with an elapsed timer and the typical-time hint. In a browser it shows the in-session slash
 * command that does the same, and the item's latest brief when there is one.
 *
 * In a contract-3 app (epic PI-SPEC-SW-E284) Groom adds the item to the run
 * queue: an item already queued or running shows its queue badge instead of
 * the Groom button, and the result card shows the badge until the run
 * settles, then the brief.
 *
 * Since epic PI-SPEC-SW-E290 (feature PI-SPEC-SW-1482, UX-PM-04, frame B4)
 * the app puts a single item next in line, and while it waits in Up next the
 * page shows "AB#… is next in line" (NextInLineCard) with See it in the
 * Queue and Cancel; once it starts, the running display above takes over.
 */

/** The queue-jump clause of One item's sentence (UX-PM-04, frame B4), shown in white bold. */
export const ONE_ITEM_QUEUE_JUMP = "goes ahead of everything waiting in the Queue";

/**
 * What One item is for (UX-PM-25, frame 1d). PKG-PM-A shipped it without the
 * mockup's queue-jump clause (01-roles.md §9.6); PKG-PM-B (epic
 * PI-SPEC-SW-E290, UX-PM-04, frame B4) adds it back with the queue jump it
 * describes: a single item goes next in line.
 */
export const ONE_ITEM_PURPOSE = `For an urgent item that cannot wait for the next session. Claude reads the code on this computer — usually 3 to 6 minutes — and ${ONE_ITEM_QUEUE_JUMP}. Nothing is posted until you choose Post.`;

/** {@link ONE_ITEM_PURPOSE} without the queue jump, for an app that does not put single items next in line. */
export const ONE_ITEM_PURPOSE_PLAIN =
  "For an urgent item that cannot wait for the next session. Claude reads the code on this computer — usually 3 to 6 minutes. Nothing is posted until you choose Post.";

/**
 * {@link ONE_ITEM_PURPOSE} with the queue-jump clause in white bold (frame
 * B4), only when the app's queue shows it puts single items next in line
 * (`appPutsSingleItemsNextInLine`); else {@link ONE_ITEM_PURPOSE_PLAIN}.
 */
function OneItemPurpose({ queueJump }: { queueJump: boolean }) {
  if (!queueJump) return <>{ONE_ITEM_PURPOSE_PLAIN}</>;
  const [before, after] = ONE_ITEM_PURPOSE.split(ONE_ITEM_QUEUE_JUMP);
  return (
    <>
      {before}
      <b className="font-semibold text-foreground">{ONE_ITEM_QUEUE_JUMP}</b>
      {after}
    </>
  );
}

/** The sentence for people without `groom-one`, who can only read the latest brief. */
export const ONE_ITEM_READ_PURPOSE = "Type an item number to read its latest brief.";

export interface ParsedItem {
  id: string;
  /** The ADO project, when the input was an ADO link. */
  project?: string;
}

const ID_PATTERN = /^(?:AB#)?([1-9]\d{0,9})$/i;
const URL_PATTERN = /\/([^/?#]+)\/_workitems\/edit\/([1-9]\d{0,9})(?:[/?#]|$)/i;

/** Read an item id from `24606`, `AB#24606` or an ADO work item link; null when it is none of those. */
export function parseItemInput(input: string): ParsedItem | null {
  const value = input.trim();
  const idMatch = ID_PATTERN.exec(value);
  if (idMatch?.[1]) return { id: idMatch[1] };
  const urlMatch = URL_PATTERN.exec(value);
  if (urlMatch?.[1] && urlMatch[2]) {
    let project: string;
    try {
      project = decodeURIComponent(urlMatch[1]);
    } catch {
      return null;
    }
    return { id: urlMatch[2], project };
  }
  return null;
}

type BatchEndEvent = Extract<GroomingRunnerEvent, { type: "batch-finished" | "batch-stopped" }>;

/** The contract 1-2 single-item batch a run waits on (see `groomOneBatch`). */
interface GroomOneBatch {
  waiting: boolean;
  batchId: string | null;
  early: BatchEndEvent[];
}

const NO_GROOM_ONE_BATCH: GroomOneBatch = { waiting: false, batchId: null, early: [] };

type RunState =
  | { status: "idle" }
  /** Reading the item to see whether it already has a brief (E289, UX-PM-25). */
  | { status: "checking" }
  /** The item already has a brief: announced, nothing queued until "Get a new brief anyway". */
  | { status: "existing"; item: WorkItemDetail; brief: GroomingBrief; stale: boolean }
  | { status: "running"; startedAt: number }
  | { status: "briefed"; minutes: number }
  | { status: "not-ready" }
  | { status: "failed"; cause: string; whatToDo: string; fixPath?: string | null };

/** "about 3 points" / "about 1 point". */
function aboutPoints(points: number): string {
  return `about ${String(points)} ${points === 1 ? "point" : "points"}`;
}

/**
 * The announcement line of an existing brief (frame 1d): "Prepared Oct 12 ·
 * about 3 points, could be 2–5 · not sure yet · posted to ADO Oct 12 by
 * Aaron Gorsky. The item has not changed since." The posted part shows only
 * once the brief is in ADO. Pure; exported for tests.
 */
export function existingBriefMeta(
  brief: Pick<GroomingBrief, "createdAt" | "createdBy" | "suggestedPoints" | "pointsLow" | "pointsHigh" | "confidence" | "commentStatus" | "postedAt" | "postedBy">,
  stale: boolean
): string {
  const size =
    brief.pointsLow === brief.pointsHigh
      ? aboutPoints(brief.suggestedPoints)
      : `${aboutPoints(brief.suggestedPoints)}, could be ${String(brief.pointsLow)}–${String(brief.pointsHigh)}`;
  const parts = [`Prepared ${briefDay(brief.createdAt)}`, size, confidenceWords(brief.confidence).toLowerCase()];
  if (briefStateOf({ commentStatus: brief.commentStatus }) === "posted") {
    const by = brief.postedBy?.name ?? brief.createdBy?.name ?? null;
    parts.push(`posted to ADO ${briefDay(brief.postedAt ?? brief.createdAt)}${by ? ` by ${by}` : ""}`);
  }
  return `${parts.join(" · ")}. ${stale ? "The item changed since." : "The item has not changed since."}`;
}

/** Frame 1d's card: the item already has a brief. "Open this brief" first, "Get a new brief anyway" second. */
function ExistingBriefCard({
  project,
  item,
  brief,
  stale,
  onGetNew,
  busy,
}: {
  project: string;
  item: WorkItemDetail;
  brief: GroomingBrief;
  stale: boolean;
  onGetNew: () => void;
  busy: boolean;
}) {
  return (
    <div
      className="flex flex-col gap-3 rounded-[10px] border border-[#1E3A5F] bg-[#0D1520] px-[18px] py-4"
      data-testid="one-item-existing"
    >
      <div className="flex items-center gap-2.5">
        <Info className="h-4 w-4 shrink-0 text-[#6CC4FF]" aria-hidden="true" />
        <p className="min-w-0 flex-1 text-sm font-semibold">AB#{item.ref.id} already has a brief</p>
        <BriefStateChip state={briefStateOf({ commentStatus: brief.commentStatus, stale })} />
      </div>
      <p className="truncate text-[13px]" title={item.title}>
        {item.title}
      </p>
      <p className="text-[13px] text-muted-foreground" data-testid="one-item-existing-meta">
        {existingBriefMeta(brief, stale)}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button asChild variant="outline" size="sm" className="gap-1.5">
          <Link to={groomingPath(project, `items/${item.ref.id}`)}>
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
            Open this brief
          </Link>
        </Button>
        <Button type="button" variant="outline" size="sm" className="gap-1.5" disabled={busy} onClick={onGetNew}>
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          Get a new brief anyway
        </Button>
      </div>
    </div>
  );
}

/** The state text colour of a recent item (frame 1d): blue until posted, amber when it needs attention, grey otherwise. */
const RECENT_STATE_CLASS: Record<RecentItemState, string> = {
  "not-posted": "text-[#6CC4FF]",
  posted: "text-muted-foreground",
  "held-back": "text-muted-foreground",
  "out-of-date": "text-[#FBBF24]",
  "needs-information": "text-[#FBBF24]",
  "no-brief": TOOLBOX_TEXT.faint,
};

/**
 * Frame 1d's "Recently opened": items opened on this computer, newest first,
 * each with the state it had when it was last read here (no backlog read); a
 * click fills and submits one.
 */
function RecentlyOpened({
  items,
  showProject,
  onOpen,
  disabled,
}: {
  items: RecentItem[];
  showProject: boolean;
  onOpen: (item: RecentItem) => void;
  disabled: boolean;
}) {
  if (items.length === 0) return null;
  return (
    <section className="flex flex-col gap-2" aria-labelledby="one-item-recent-heading" data-testid="one-item-recent">
      <h2 id="one-item-recent-heading" className={TOOLBOX_TEXT.eyebrow}>
        Recently opened
      </h2>
      <ul className="glass-card overflow-hidden rounded-[10px]">
        {items.map((item) => {
          const { state } = item;
          return (
            <li key={`${item.project}/${item.itemId}`} className="border-t border-[#1F1F23] first:border-t-0">
              <button
                type="button"
                className="grid w-full grid-cols-[64px_minmax(0,1fr)_140px] items-center gap-3 px-4 py-2.5 text-left hover:bg-[#1A1A1E] disabled:opacity-60"
                onClick={() => {
                  onOpen(item);
                }}
                disabled={disabled}
                data-testid="one-item-recent-row"
              >
                <span className="font-mono text-xs text-muted-foreground">{item.itemId}</span>
                <span className="truncate text-[13px]" title={item.title}>
                  {item.title || `AB#${item.itemId}`}
                  {showProject && <span className={cn("ml-2 text-xs", TOOLBOX_TEXT.faint)}>{item.project}</span>}
                </span>
                <span className={cn("truncate text-xs", state ? RECENT_STATE_CLASS[state] : TOOLBOX_TEXT.faint)} data-state={state}>
                  {state ? recentItemStateWords(state) : ""}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

const selectClassName =
  "h-10 rounded-lg border border-[#3F3F46] bg-[#111113] px-3 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50";

/**
 * How a contract 1-2 single-item batch that sent no `item-finished` ended: its
 * item was unchanged since its brief (nothing ran, nothing failed), below the
 * bar, or the batch failed, stopped or was canceled.
 */
function batchEndOutcome(event: BatchEndEvent, project: string): "unchanged" | RunState {
  if (event.type === "batch-stopped") {
    if (event.reason === "user") return { status: "idle" };
    const message = event.errorCode ? groomingErrorMessage(event.errorCode) : stopReasonMessage(event.reason);
    const fixPath = groomingFixPath(event.errorCode ?? (event.reason === "environment" ? "environment" : null), project);
    return { status: "failed", cause: message.cause, whatToDo: message.whatToDo, fixPath };
  }
  if (event.status === "canceled") return { status: "idle" };
  if (event.notReady > 0) return { status: "not-ready" };
  if (event.failed > 0) {
    const message = groomingErrorMessage(null);
    return { status: "failed", cause: message.cause, whatToDo: message.whatToDo };
  }
  return "unchanged";
}

/** `/grooming/:project/one` and `/grooming/one`. */
export function GroomingOneItemPage() {
  const { project: routeProject } = useParams();
  usePageTitle("One item");
  const queryClient = useQueryClient();
  const { runner, support } = useRunnerBridge();
  const projects = useGroomingProjects();
  const [input, setInput] = useState("");
  const [chosenProject, setChosenProject] = useState("");
  const [target, setTarget] = useState<{ project: string; id: string } | null>(null);
  const [run, setRun] = useState<RunState>({ status: "idle" });
  const [inputError, setInputError] = useState<string | null>(null);

  const supportsProgress = useSupportsRunProgress();
  const session = useProjectSession(run.status === "running" ? target?.project : undefined);
  const progress = useRunProgress(runner, { itemId: run.status === "running" ? (target?.id ?? null) : null });

  const fallbackProject = routeProject ?? (chosenProject !== "" ? chosenProject : (projects.data?.[0]?.project ?? ""));
  const parsed = parseItemInput(input);
  const needsProjectPick = !routeProject && !parsed?.project;
  const { data: access } = useToolboxAccess();
  const targetProject = parsed?.project ?? fallbackProject;
  const mayGroom = canOn(access, targetProject, "groom-one");
  const groomRunner = mayGroom ? runner : null;
  // E283: "Working as" before Groom when the person's roles there carry more than one persona.
  const persona = usePersonaChoice(mayGroom && targetProject !== "" ? targetProject : null);

  // E284: the run queue. The typed item's key decides whether Groom is offered or its badge shows.
  const { state: queueState, actions: queueActions } = useGroomingQueue();
  const inputKey = mayGroom && parsed ? safeQueueKey("groom-item", targetProject, parsed.id) : null;
  const queue = useQueueStart(inputKey, () => queueActions.enqueueItem(targetProject, parsed?.id ?? ""));
  const queueMode = queue.available && groomRunner !== null;
  const targetKey = target ? safeQueueKey("groom-item", target.project, target.id) : null;
  // "Get a new brief anyway" queues the announced item, even when the input was edited since.
  const targetQueue = useQueueStart(canOn(access, target?.project ?? "", "groom-one") ? targetKey : null, () =>
    queueActions.enqueueItem(target?.project ?? "", target?.id ?? "")
  );
  // Follows the announced item, not the input: editing the input while it runs must not end it.
  const targetActive = targetQueue.available ? activeEntryByKey(queueState, targetKey) : null;
  // The target's queue entry: seen queued or running, or named by the enqueue that added it.
  const targetEntryId = useRef<string | null>(null);
  if (targetActive) targetEntryId.current = targetActive.entry.id;
  // Each submit gets a number; a slow item read from an older submit is ignored.
  const submitSeq = useRef(0);
  // F1: SpecTree makes no new brief for an item unchanged since its brief (it marks the run `current`),
  // and neither the queue nor the batch events say so. So "Get a new brief anyway" keeps the brief it
  // set out to replace, each run that finishes notes when, and once the item is read again after that,
  // the same latest brief means no new one came.
  const [replacing, setReplacing] = useState<{ briefId: string; settledAt: number | null } | null>(null);
  const noteRunFinished = useCallback(() => {
    setReplacing((prev) => (prev ? { ...prev, settledAt: Date.now() } : prev));
  }, []);
  // Contract 1-2 (no queue): the single-item batch `groomOne` started. Its `batch-finished` or
  // `batch-stopped` ends a run that sends no `item-finished` (an unchanged or below-bar item). A batch
  // end that comes before `groomOne` returns the batch id is kept in `early` until it does.
  const groomOneBatch = useRef<GroomOneBatch>(NO_GROOM_ONE_BATCH);

  // E289 (UX-PM-25): remember every item opened here, with its state, for "Recently opened".
  // It rides on the item read the result card or the check already makes (no new ADO read).
  // Each submit (a new `target`) is an open and moves the item to the front; a later read of
  // the same target (a refetch after a run or a post) only updates its state in place.
  const targetItem = useWorkItem("ado", target?.project, target?.id, { rideAlong: true });
  const recordedTarget = useRef<{ project: string; id: string } | null>(null);
  useEffect(() => {
    const item = targetItem.data;
    if (!target || item?.ref.id !== target.id) return;
    const block = item.groomingBrief ?? null;
    const entry = {
      project: target.project,
      itemId: target.id,
      title: item.title,
      state: recentItemState({
        stage: stageOfItem(item),
        aiSuggestion: block?.brief ? { commentStatus: block.brief.commentStatus, stale: block.stale } : null,
      }),
    };
    if (recordedTarget.current === target) {
      updateRecentItem(entry);
      return;
    }
    recordedTarget.current = target;
    recordRecentItem(entry);
  }, [target, targetItem.data]);

  // The list: this project's items (every project's on /grooming/one), each with its remembered state.
  const recent = useRecentItems(routeProject);

  // A click on a recent item fills the form, then submits it on the next render, when the
  // derived project, role and queue key all describe that item.
  const [submitRequested, setSubmitRequested] = useState(false);
  const targetBadge = useQueueBadgeStatus(targetQueue.available ? targetKey : null);
  const handledEntryId = useRef<string | null>(null);
  /** The target's queue entry left the queue: finished, or removed. Runs once per entry. */
  const onTargetEntrySettled = (entryId: string | null) => {
    if (!target || entryId === null || handledEntryId.current === entryId) return;
    handledEntryId.current = entryId;
    // Only a run that finished as briefed can have left the brief as it was; a failed, stopped or removed one says nothing.
    const settled = queueState?.completed.find((entry) => entry.id === entryId);
    if (settled?.state === "briefed") noteRunFinished();
    else setReplacing(null);
    // The announced brief was replaced by the run that just settled: show the item's latest brief instead.
    setRun((prev) => (prev.status === "existing" ? { status: "idle" } : prev));
    void queryClient.invalidateQueries({ queryKey: workItemKeys.detail("ado", target.project, target.id) });
    void queryClient.invalidateQueries({ queryKey: groomingKeys.project(target.project) });
  };
  const onTargetEntrySettledLatest = useRef(onTargetEntrySettled);
  onTargetEntrySettledLatest.current = onTargetEntrySettled;
  const queueStateLatest = useRef(queueState);
  queueStateLatest.current = queueState;
  /** A fast run (an unchanged item settles at once) can first show up already finished, never seen running. */
  const checkTargetEntryFinished = useCallback(() => {
    const entryId = targetEntryId.current;
    if (entryId !== null && queueStateLatest.current?.completed.some((entry) => entry.id === entryId)) {
      onTargetEntrySettledLatest.current(entryId);
    }
  }, []);
  useOnQueueSettled(targetActive?.entry.id ?? null, () => {
    onTargetEntrySettled(targetEntryId.current);
  });
  useEffect(() => {
    checkTargetEntryFinished();
  }, [queueState, checkTargetEntryFinished]);

  const onEvent = useCallback(
    (event: GroomingRunnerEvent) => {
      if (!target) return;
      const refresh = () => {
        void queryClient.invalidateQueries({ queryKey: workItemKeys.detail("ado", target.project, target.id) });
        void queryClient.invalidateQueries({ queryKey: groomingKeys.project(target.project) });
      };
      // Contract 1-2: an item SpecTree did not run (unchanged since its brief, or below the bar) sends no
      // `item-finished`, only its batch's end; without this the page stays on "Getting a brief…".
      if (event.type === "batch-finished" || event.type === "batch-stopped") {
        const batch = groomOneBatch.current;
        if (!batch.waiting) return;
        if (batch.batchId === null) {
          batch.early.push(event);
          return;
        }
        if (batch.batchId !== event.batchId) return;
        groomOneBatch.current = NO_GROOM_ONE_BATCH;
        const end = batchEndOutcome(event, target.project);
        if (end === "unchanged") noteRunFinished();
        else setReplacing(null);
        setRun((prev) => (prev.status !== "running" ? prev : end === "unchanged" ? { status: "idle" } : end));
        refresh();
        return;
      }
      if (event.type !== "item-finished" || event.itemId !== target.id) return;
      // This event ends the run, so the batch's end that follows is not needed. A brief it made is new.
      groomOneBatch.current = NO_GROOM_ONE_BATCH;
      setReplacing(null);
      setRun((prev) => {
        if (prev.status !== "running") return prev;
        if (event.outcome === "briefed") {
          return { status: "briefed", minutes: Math.max(1, Math.round((Date.now() - prev.startedAt) / 60000)) };
        }
        if (event.outcome === "not-ready") return { status: "not-ready" };
        const message = groomingErrorMessage(event.errorCode);
        return { status: "failed", cause: message.cause, whatToDo: message.whatToDo, fixPath: groomingFixPath(event.errorCode, target.project) };
      });
      refresh();
    },
    [target, queryClient, noteRunFinished]
  );
  useRunnerEvents(runner, onEvent);
  const onEventLatest = useRef(onEvent);
  onEventLatest.current = onEvent;

  /** Start the run for `next`: add it to the run queue, or run it on the runner (contract 1-2). */
  const startRun = (
    next: { project: string; id: string },
    start: { enqueue: (() => Promise<GroomingQueueActionResult | null>) | null; runner: NonNullable<typeof runner> }
  ) => {
    const { enqueue } = start;
    if (enqueue) {
      // E284: add it to the run queue; the result card follows the queue entry.
      setRun({ status: "idle" });
      void persona
        .ensureSaved(next.project)
        .then(
          () => enqueue(),
          () => null
        )
        .then((result) => {
          if (!result?.ok || !result.entryId) return;
          targetEntryId.current = result.entryId;
          checkTargetEntryFinished();
        });
      return;
    }
    const starter = start.runner;
    // This run's own tracker: a late reply from an earlier run must not touch a newer one.
    const mine: GroomOneBatch = { waiting: true, batchId: null, early: [] };
    groomOneBatch.current = mine;
    const isCurrent = () => groomOneBatch.current === mine;
    setRun({ status: "running", startedAt: Date.now() });
    // The persona is saved in SpecTree first; SpecTree stamps it on the one-item batch.
    void persona
      .ensureSaved(next.project)
      .then(
        () => starter.groomOne(next.project, next.id),
        () => {
          if (isCurrent()) {
            groomOneBatch.current = NO_GROOM_ONE_BATCH;
            setRun({ status: "idle" });
          }
          return null;
        }
      )
      .then(
        (result) => {
          if (result === null || !isCurrent()) return;
          if (result.ok) {
            mine.batchId = result.batchId;
            // The batch's end may have come first.
            const ended = mine.early.find((event) => event.batchId === result.batchId);
            mine.early = [];
            if (ended) onEventLatest.current(ended);
            return;
          }
          groomOneBatch.current = NO_GROOM_ONE_BATCH;
          const message = groomingErrorMessage(result.errorCode);
          setRun({ status: "failed", cause: message.cause, whatToDo: message.whatToDo, fixPath: groomingFixPath(result.errorCode, next.project) });
        },
        () => {
          if (!isCurrent()) return;
          groomOneBatch.current = NO_GROOM_ONE_BATCH;
          setRun({ status: "failed", cause: "SpecTree Toolbox could not start the run.", whatToDo: "Try again in a moment." });
        }
      );
  };

  /**
   * The item read (E289, UX-PM-25): the same query the brief review reads, so
   * no second ADO read. Null when it fails; the run then starts as before.
   */
  const readItem = (next: { project: string; id: string }): Promise<WorkItemDetail | null> =>
    queryClient
      .fetchQuery({
        queryKey: workItemKeys.detail("ado", next.project, next.id),
        queryFn: () => workItemsApi.detail("ado", next.project, next.id),
        // Always read ADO again: a cached copy says "not changed" after the item was edited.
        staleTime: 0,
        retry: false,
      })
      .then(
        (response) => response.data,
        () => null
      );

  const submit = () => {
    if (!parsed) {
      setInputError("Enter an item number, AB#number or an Azure DevOps work item link.");
      return;
    }
    const project = parsed.project ?? fallbackProject;
    if (!project) {
      setInputError("Choose the project the item belongs to.");
      return;
    }
    setInputError(null);
    const next = { project, id: parsed.id };
    setTarget(next);
    setReplacing(null);
    targetEntryId.current = null;
    submitSeq.current += 1;
    // An item already queued or running shows its place in the queue (the result card); it is never queued twice.
    if (!groomRunner || (queueMode && queue.active)) {
      setRun({ status: "idle" });
      return;
    }
    const start = { enqueue: queueMode ? queue.enqueue : null, runner: groomRunner };
    // Before Claude runs, say when the item already has a brief; an item without one starts at once.
    const seq = submitSeq.current;
    setRun({ status: "checking" });
    void readItem(next).then((item) => {
      if (seq !== submitSeq.current) return;
      const block = item?.groomingBrief ?? null;
      if (item && block?.brief) {
        setRun({ status: "existing", item, brief: block.brief, stale: block.stale });
        return;
      }
      startRun(next, start);
    });
  };

  const submitLatest = useRef(submit);
  submitLatest.current = submit;
  useEffect(() => {
    if (!submitRequested) return;
    setSubmitRequested(false);
    submitLatest.current();
  }, [submitRequested]);

  const openRecent = (item: RecentItem) => {
    setInput(item.itemId);
    if (!routeProject) setChosenProject(item.project);
    setSubmitRequested(true);
  };

  // The item as last read: a refetch (after an ADO edit, a post or a run elsewhere) updates the card and the notice.
  const liveItem = target && targetItem.data?.ref.id === target.id ? targetItem.data : null;
  const liveBlock = liveItem?.groomingBrief ?? null;
  const existingCard =
    run.status !== "existing"
      ? null
      : liveItem && liveBlock?.brief
        ? { item: liveItem, brief: liveBlock.brief, stale: liveBlock.stale }
        : { item: run.item, brief: run.brief, stale: run.stale };
  const settledAt = replacing?.settledAt ?? null;
  const noNewBrief =
    settledAt !== null &&
    !targetItem.isFetching &&
    targetItem.dataUpdatedAt > settledAt &&
    liveBlock?.brief?.id === replacing?.briefId;

  /** "Get a new brief anyway": the deliberate second choice on the announcement card. */
  const getNewBriefAnyway = () => {
    if (!target || !runner || !canOn(access, target.project, "groom-one")) return;
    submitSeq.current += 1;
    setReplacing(existingCard ? { briefId: existingCard.brief.id, settledAt: null } : null);
    startRun(target, { enqueue: targetQueue.available ? targetQueue.enqueue : null, runner });
  };

  return (
    <div className="flex w-full flex-col gap-5 pt-9" data-testid="one-item">
      <div className="flex flex-col gap-1.5">
        <h1 className={TOOLBOX_PAGE_TITLE}>{mayGroom ? "Get a brief for one item" : "Read a brief for one item"}</h1>
        <p className="text-sm leading-[1.55] text-muted-foreground" data-testid="one-item-purpose">
          {mayGroom ? <OneItemPurpose queueJump={appPutsSingleItemsNextInLine(queueState)} /> : ONE_ITEM_READ_PURPOSE}
        </p>
      </div>
      <form
        className="flex flex-wrap items-center gap-2.5"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {needsProjectPick && (projects.data?.length ?? 0) > 0 && (
          <select
            aria-label="Project"
            className={selectClassName}
            value={fallbackProject}
            onChange={(event) => {
              setChosenProject(event.target.value);
            }}
          >
            {(projects.data ?? []).map((p) => (
              <option key={p.project} value={p.project}>
                {p.project}
              </option>
            ))}
          </select>
        )}
        <label className="sr-only" htmlFor="one-item-input">
          Item number or ADO link
        </label>
        <input
          id="one-item-input"
          value={input}
          onChange={(event) => {
            setInput(event.target.value);
          }}
          placeholder="24606, AB#24606 or https://dev.azure.com/…/_workitems/edit/24606"
          className="h-10 min-w-0 flex-1 rounded-lg border border-[#3F3F46] bg-[#111113] px-3.5 font-mono text-[13px] text-foreground placeholder:text-[#8B8B94] focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
        {queueMode && queue.active ? (
          <QueueBadge status={queue.active} className="h-10 rounded-lg px-3 text-xs" />
        ) : (
          <Button
            type="submit"
            className="h-9 gap-1.5 border-primary bg-primary px-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
            disabled={input.trim() === "" || run.status === "running" || run.status === "checking" || queue.pending || targetQueue.pending}
          >
            {groomRunner && <Sparkles className="h-4 w-4" aria-hidden="true" />}
            {groomRunner ? (run.status === "running" ? "Getting a brief…" : "Get a brief") : "Show"}
          </Button>
        )}
      </form>
      <PersonaPicker choice={persona} />
      {inputError && (
        <p className="text-sm text-destructive" role="alert">
          {inputError}
        </p>
      )}
      {queueMode && (queue.error ?? targetQueue.error) && (
        <p className="text-sm text-destructive" role="alert">
          {queue.error ?? targetQueue.error}
        </p>
      )}

      {mayGroom && !runner && (
        <div className="space-y-3" data-testid="one-item-browser">
          <RunnerFallback support={support} />
          {target && (
            <CommandCopyBox
              label="Groom this item in Claude Code"
              command={groomCommand(target.id)}
              helperText="Run this in Claude Code inside the code repository. The brief is saved in SpecTree and shows here."
            />
          )}
        </div>
      )}

      {target && existingCard && !targetActive && (
        <ExistingBriefCard
          project={target.project}
          item={existingCard.item}
          brief={existingCard.brief}
          stale={existingCard.stale}
          onGetNew={getNewBriefAnyway}
          busy={targetQueue.pending}
        />
      )}

      {target && run.status === "checking" && (
        <p className="text-[13px] text-muted-foreground" role="status" data-testid="one-item-checking">
          Checking AB#{target.id}…
        </p>
      )}

      {/* E290 (UX-PM-04, frame B4): while the item waits in Up next, say where it is ("AB#… is next in line"). */}
      {target && run.status !== "checking" && targetActive?.kind === "queued" && (
        <NextInLineCard entryId={targetActive.entry.id} project={target.project} title={liveItem?.title} />
      )}

      {/* An announced item that is running (from here or elsewhere) shows its place instead of the card. */}
      {target && run.status !== "checking" && targetActive?.kind !== "queued" && (run.status !== "existing" || targetActive) && (
        // The card spans the page; its side padding keeps the brief in a readable 880 px column.
        <div className="glass-card rounded-[10px] px-[max(1.5rem,calc((100%-880px)/2))] py-6" data-testid="one-item-result">
          {targetActive ? (
            <div className="space-y-3" data-testid="one-item-queued">
              <p className="font-mono text-xs text-muted-foreground">AB#{target.id}</p>
              <QueueBadge status={targetActive} />
              <p className="text-sm text-muted-foreground">
                The brief appears here when Claude finishes. Nothing is posted until you choose Post to ADO.
              </p>
            </div>
          ) : run.status === "running" ? (
            <div className="space-y-3" data-testid="one-item-running">
              <p className="font-mono text-xs text-muted-foreground">AB#{target.id}</p>
              <RunActivity
                progress={progress}
                startedAt={run.startedAt}
                typicalItemSeconds={session.data?.typicalItemSeconds ?? null}
                supportsProgress={supportsProgress}
                note="The brief appears here when Claude finishes. Nothing is posted until you choose Post to ADO."
              />
            </div>
          ) : run.status === "failed" ? (
            <div className="space-y-2" role="alert" data-testid="one-item-failed">
              <p className="font-semibold">Couldn&apos;t finish this brief</p>
              <p className="text-sm">{run.cause}</p>
              <p className="text-sm text-muted-foreground">{run.whatToDo}</p>
              {run.fixPath && (
                <Link to={run.fixPath} className="text-sm text-link hover:underline" data-testid="one-item-fix-it">
                  Fix it
                </Link>
              )}
            </div>
          ) : run.status === "not-ready" ? (
            <div className="space-y-2" data-testid="one-item-not-ready">
              <p className="font-semibold text-amber-300">Not ready to groom</p>
              <p className="text-sm">This item does not have what the team needs to size it yet.</p>
              <Link
                to={formulatorImprovePath(target.project, target.id)}
                className="text-sm text-link hover:underline"
              >
                Fix in ADO Formulator
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {targetBadge && <QueueBadge status={targetBadge} />}
              {run.status === "briefed" && (
                <p className="text-xs text-muted-foreground" data-testid="brief-finished">
                  Ready in {run.minutes} min
                </p>
              )}
              {noNewBrief && (
                <p className="text-sm text-muted-foreground" role="status" data-testid="one-item-unchanged">
                  No new brief: the item has not changed since.
                </p>
              )}
              <BriefReview key={`${target.project}/${target.id}`} project={target.project} itemId={target.id} allowCopy />
            </div>
          )}
        </div>
      )}

      <RecentlyOpened
        items={recent}
        showProject={!routeProject}
        onOpen={openRecent}
        disabled={run.status === "running" || run.status === "checking" || queue.pending || targetQueue.pending}
      />
    </div>
  );
}
